import {assert} from 'Asserts';
import {TagSchema} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {
  RegistrationOperation,
  RegistrationOperationType,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {RegistrationException} from 'notebook/registration-tables/RegistrationTableUtil';
import {
  ColumnDefinition,
  SchemaFieldColumnDefinition,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  convertTypedValueToTag,
  formatTypedValue,
  getBioentityInfoFromTypedValue,
  isTypedValueEmpty,
} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getTagUpdateParams} from 'schemas/tags/TagUtil';

// TODO(BNCH-4551): We should use an error object for proper cell evaluation.
export const LOADING = 'Loading...';

/**
 * Some column types should never be editable.
 */
export function isAlwaysLocked(columnDefinition: ColumnDefinition): boolean {
  switch (columnDefinition.type) {
    case TableColumnType.BATCH:
      return true;
    case TableColumnType.TAG_VALUE:
      return columnDefinition.tagSchemaFieldIsComputed || columnDefinition.tagSchemaFieldIsConvertedFromLink;
    default:
      return false;
  }
}

export function isBioentityFieldColumn(
  columnDefinition: SchemaFieldColumnDefinition,
  tagSchema: TagSchema
): boolean {
  return tagSchema && tagSchema.fields.some((field) => field.apiId === columnDefinition.tagSchemaFieldApiId);
}

function isBatchFieldColumn(
  columnDefinition: SchemaFieldColumnDefinition,
  batchSchema: BatchSchema | null,
  tableType: RegistrationTableType
): boolean {
  return (
    tableType === RegistrationTableType.BATCH &&
    batchSchema != null &&
    batchSchema.fields.some((field: SchemaField) => field.apiId === columnDefinition.tagSchemaFieldApiId)
  );
}

function isExistingFileOperation(operation: RegistrationOperation): boolean {
  return [
    RegistrationOperationType.ADD_EXISTING_FILE,
    RegistrationOperationType.ADD_BATCH_TO_EXISTING_FILE,
  ].includes(operation.operationType);
}

/**
 * Given an existing register operation and a typedValue of the evaluated cell, return
 * a new register operation from applying the value specified in the cell. See
 * /entries/<api_identifier>/register-samples for the format of a register operation.
 */
export function augmentRegisterOperation({
  operation,
  typedValue,
  columnDefinition,
  tagSchema,
  batchSchema,
  tableType,
}: {
  operation: RegistrationOperation;
  typedValue: TypedValue;
  columnDefinition: ColumnDefinition;
  tagSchema: TagSchema;
  batchSchema: BatchSchema | null;
  tableType: RegistrationTableType;
}): RegistrationOperation {
  switch (columnDefinition.type) {
    case TableColumnType.BATCH:
      return operation;
    case TableColumnType.STAGE_RUN:
      // eslint-disable-next-line benchlint/no-in-operator
      if (!('stageRunId' in typedValue) || !typedValue.stageRunId) {
        throw new RegistrationException(`Invalid stage run name: ${formatTypedValue(typedValue)}`);
      }
      return {
        ...operation,
        stageRunId: typedValue.stageRunId,
      };
    case TableColumnType.EXISTING_BIOENTITY: {
      const bioentityInfo = getBioentityInfoFromTypedValue(typedValue);
      if (!bioentityInfo) {
        return operation;
      } else {
        return {
          ...operation,
          operationType:
            tableType === RegistrationTableType.FILE
              ? RegistrationOperationType.ADD_EXISTING_FILE
              : RegistrationOperationType.ADD_BATCH_TO_EXISTING_FILE,
          fileId: bioentityInfo.id,
        };
      }
    }
    case TableColumnType.BIOENTITY_NAME: {
      if (isExistingFileOperation(operation)) {
        return operation;
      }
      const name = formatTypedValue(typedValue);
      if (name === '') {
        throw new RegistrationException('Entity Name cannot be blank.');
      }
      return {...operation, name};
    }
    case TableColumnType.DNA_BASES: {
      if (isExistingFileOperation(operation)) {
        return operation;
      }
      const bases = formatTypedValue(typedValue);
      return {...operation, bases};
    }
    case TableColumnType.SMILES: {
      if (isExistingFileOperation(operation)) {
        return operation;
      }
      const smiles = formatTypedValue(typedValue);
      return {...operation, smiles};
    }
    case TableColumnType.TAG_VALUE: {
      // Note: RegistrationTable._getEvaluatedRowsForRegistration already handles coercion issues.
      if (isBioentityFieldColumn(columnDefinition, tagSchema) && isExistingFileOperation(operation)) {
        return operation;
      }
      if (isTypedValueEmpty(typedValue)) {
        return operation;
      }
      // The function is used for performing the registration operation so its ok to access live schemaField
      if (typedValue.type !== columnDefinition.schemaField.fieldType) {
        throw new RegistrationException(
          `The ${columnDefinition.schemaField.name} field has an invalid value.`
        );
      }

      // The function is used for performing the registration operation so its ok to access live schemaField
      const tagValue = convertTypedValueToTag(typedValue, columnDefinition.schemaField);
      assert(tagValue != null, 'Unexpected null tag value', {typedValue, columnDefinition});
      if (isBioentityFieldColumn(columnDefinition, tagSchema)) {
        return {
          ...operation,
          tagsDict: {
            ...operation.tagsDict,
            [columnDefinition.schemaField.name]: getTagUpdateParams(tagValue),
          },
        };
      } else if (isBatchFieldColumn(columnDefinition, batchSchema, tableType)) {
        return {
          ...operation,
          batchTagsDict: {
            ...operation.batchTagsDict,
            [columnDefinition.schemaField.name]: getTagUpdateParams(tagValue),
          },
        };
      } else {
        throw new Error(
          `Schema field ID ${columnDefinition.tagSchemaFieldApiId} was not found in the table's tag schema or ` +
            `batch schema`
        );
      }
    }
    case TableColumnType.MISSING_FIELD:
      return operation;
    default:
      assert(false, `Unsupported column type ${columnDefinition.type}`);
  }
}
